import '@react-pdf-viewer/core/lib/styles/index.css';
// eslint-disable-next-line import/no-unresolved,import/no-extraneous-dependencies
import '@react-pdf-viewer/zoom/lib/styles/index.css';

import { Viewer, Worker } from '@react-pdf-viewer/core';
// eslint-disable-next-line import/no-extraneous-dependencies
import { zoomPlugin } from '@react-pdf-viewer/zoom';
import { ArrowRight } from 'lucide-react';
import React, { FC, Fragment, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams } from 'react-router-dom';
import { CoverImage, Error, LightboxGallery, Modal, Spinner } from 'src/components';
import { getLocalizedLangValue } from 'src/helpers';
import {
  useGetBusinessCardAttachmentsFromFirebase,
  useGetBusinessCardFromFirestore,
  useQueryHelper,
} from 'src/queries';
import { useLanguageState, useOrganizationState } from 'src/state';
import { Field } from 'src/types';
import { Swiper, SwiperSlide } from 'swiper/react';

import {
  ActionContainer,
  Image,
  OfferContainer,
  OfferImage,
  OfferModalButton,
  OfferModalButtonTitle,
  OfferTitle,
  Section,
  Text,
  Title,
} from './Business.styled';

export const Business: FC = () => {
  const { id } = useParams();
  const { organizationData } = useOrganizationState();
  const [showModal, setShowModal] = useState<string | null>(null);
  const [pdfModal, setPdfModal] = useState<string | null>(null);
  const { language: currentLang } = useLanguageState();
  const BusinessDocument = useGetBusinessCardFromFirestore(organizationData.hotel || '', id || '');
  const attachmentDocuments = useGetBusinessCardAttachmentsFromFirebase(organizationData.hotel || '', id || '');
  const { data: businessData, refetch } = BusinessDocument;
  const { data: attachmentData } = attachmentDocuments;
  const { isLoading, isError } = useQueryHelper({ queries: [BusinessDocument] });
  const { isLoading: isLoadingAttachments, isError: isErrorAttachments } = useQueryHelper(
    { queries: [attachmentDocuments] },
  );

  const zoomPluginInstance = zoomPlugin();
  const { ZoomInButton, ZoomOutButton, ZoomPopover } = zoomPluginInstance;

  const handleOpenPdf = (url: string, isUrl: boolean): void => {
    if (!isUrl) {
      setPdfModal(url);
    } else {
      window.open(url, '_blank');
    }
  };

  if (isLoading || isLoadingAttachments) {
    return <Spinner global />;
  }

  if (isError || isErrorAttachments) {
    return (<Error errorMessage="Błąd pobierania danych" action={refetch} />
    );
  }

  return (
    <>
      <Helmet>
        <title>Hotelspot | Szczegóły karty biznesowej</title>
        <meta name="description" content="Hotelspot - karta biznesowa" />
      </Helmet>
      <div className="business_screen">
        <Modal
          size="2xl"
          isOpen={showModal !== null}
          onClose={() => setShowModal(null)}
        >
          <Fragment key="modal">
            <Image src={showModal || ''} />
          </Fragment>
        </Modal>

        <Modal
          size="full"
          isOpen={pdfModal !== null}
          onClose={() => setPdfModal(null)}
          title={businessData?.offerName?.[currentLang] || ''}
        >
          <Fragment key="pdf-modal">
            <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.4.120/build/pdf.worker.min.js">
              <div className="flex flex-col h-[90vh]">
                <div className="flex items-center gap-2 p-2 bg-white shadow sticky top-0 z-10">
                  <ZoomOutButton />
                  <ZoomPopover />
                  <ZoomInButton />
                </div>
                <div className="flex-1 overflow-auto">
                  <Viewer fileUrl={pdfModal || ''} plugins={[zoomPluginInstance]} />
                </div>
              </div>
            </Worker>
          </Fragment>
        </Modal>

        {!!businessData && (
          <>
            <Section>
              <Title>{businessData?.title?.[currentLang] || ''}</Title>
              <CoverImage
                cover={businessData?.cover || ''}
                labels={businessData?.fields?.map((field: Field) => (
                  { value: field.value, icon: field.icon, type: field.type }
                ))}
              />
              <Text>{businessData?.description?.[currentLang] || ''}</Text>
            </Section>

            {!businessData?.offerCover || businessData?.offerCover === '' || attachmentData?.length === 0 ? null : (
              <Swiper
                spaceBetween={5}
                style={{ width: '100%' }}
                slidesPerView={1}
                loop
                centeredSlides
                breakpoints={{
                  768: {
                    slidesPerView: 1.2,
                    centeredSlides: true,
                  },
                  1024: {
                    slidesPerView: 1,
                    centeredSlides: true,
                  },
                }}
              >
                <SwiperSlide key="business-card">
                  <Section>
                    <OfferContainer>
                      <OfferImage src={businessData?.offerCover || ''} alt="offer" />
                      <OfferTitle>{businessData?.offerName?.[currentLang] || ''}</OfferTitle>
                      <ActionContainer>
                        {attachmentData && attachmentData.map((attachment) => {
                          const title = getLocalizedLangValue(attachment.name, currentLang);
                          const file = getLocalizedLangValue(attachment.fileUrl, currentLang);

                          return (
                            <OfferModalButton key={title} onClick={() => handleOpenPdf(file, attachment.custom_file)}>
                              <OfferModalButtonTitle key={title}>
                                {title}
                              </OfferModalButtonTitle>
                              <ArrowRight color="#FFF" />
                            </OfferModalButton>
                          );
                        })}
                      </ActionContainer>
                    </OfferContainer>
                  </Section>
                </SwiperSlide>
              </Swiper>
            )}

            {!!businessData?.gallery?.length && (
            <LightboxGallery urls={businessData?.gallery || []} />
            )}
          </>
        )}
      </div>
    </>
  );
};
